import type React from "react"
import { type DragEvent, useState } from "react"
import { FileText, Image, Trash } from "react-feather"
import { UPLOAD_ACCEPT } from "@/api/client/file"
import { Button } from "@/components/ui/button/button"
import { Input } from "@/components/ui/input/input"
import { Label } from "@/components/ui/label/label"
import { Textarea } from "@/components/ui/textarea/textarea"

export interface OwnerMemoFormData {
  title: string
  description: string
  /** 表示用の画像URL（DataURLまたはサーバーURL） */
  images: string[]
  /** 新規追加されたファイル（アップロード対象） */
  newFiles?: File[]
  /** 既存ファイルのID（編集時用） */
  existingFileIds?: number[]
  /** 削除対象のファイルID */
  deleteFileIds?: number[]
}

const IMAGE_FILE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|bmp|svg)$/i
const isImageFile = (url?: string, file?: File) =>
  file?.type.startsWith("image/") || (!!url && IMAGE_FILE_EXTENSION_REGEX.test(url))

interface OwnerMemoFormProps {
  formData: OwnerMemoFormData
  onFormChange: (data: OwnerMemoFormData) => void
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFilesSelect?: (files: FileList | File[]) => void
  onRemoveImage: (index: number) => void
  inputIdPrefix?: string
}

export function OwnerMemoForm({
  formData,
  onFormChange,
  onImageUpload,
  onFilesSelect,
  onRemoveImage,
  inputIdPrefix = "memo",
}: OwnerMemoFormProps) {
  const [isDragging, setIsDragging] = useState(false)
  const existingFileCount = formData.existingFileIds?.length ?? 0

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const files = event.dataTransfer.files
    if (files.length > 0) {
      onFilesSelect?.(files)
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event.dataTransfer.types.includes("Files")) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget
    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
      return
    }
    setIsDragging(false)
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${inputIdPrefix}-title`} className="text-sm font-semibold text-slate-600">
          タイトル
        </Label>
        <Input
          id={`${inputIdPrefix}-title`}
          value={formData.title}
          onChange={(e) => onFormChange({ ...formData, title: e.target.value })}
          placeholder="タイトルを入力"
          className="border-slate-300"
        />
      </div>

      <div className="space-y-2">
        <Label
          htmlFor={`${inputIdPrefix}-description`}
          className="text-sm font-semibold text-slate-600"
        >
          メモ内容
        </Label>
        <Textarea
          id={`${inputIdPrefix}-description`}
          value={formData.description}
          onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
          placeholder="メモ内容を入力"
          className="min-h-[120px] border-slate-300"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-600">
          添付ファイル{formData.images.length > 0 && ` (${formData.images.length}/3)`}
        </Label>

        {/* Image Grid */}
        {formData.images.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-2">
            {formData.images.map((image, index) => (
              <div key={image} className="relative">
                {isImageFile(image, formData.newFiles?.[index - existingFileCount]) ? (
                  <img
                    src={image}
                    alt={`Attachment ${index + 1}`}
                    className="h-24 w-full rounded-md object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-full items-center justify-center rounded-md bg-slate-100 px-2 text-slate-600">
                    <FileText className="size-8 text-slate-400" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onRemoveImage(index)}
                  className="absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                >
                  <Trash className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add Button */}
        {formData.images.length < 3 && (
          <div
            className={isDragging ? "rounded-md bg-blue-50 ring-2 ring-inset ring-blue-300" : ""}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <input
              type="file"
              accept={UPLOAD_ACCEPT}
              multiple
              onChange={onImageUpload}
              className="hidden"
              id={`${inputIdPrefix}-image-upload`}
            />
            <Button
              type="button"
              variant="outline"
              block
              onClick={() => document.getElementById(`${inputIdPrefix}-image-upload`)?.click()}
            >
              <Image className="size-4" />
              ファイルを追加 / ドロップ
            </Button>
          </div>
        )}

        {/* {formData.images.length >= 3 && (
          <p className="text-center text-sm text-slate-500">
            最大3枚まで追加できます
          </p>
        )} */}
      </div>
    </div>
  )
}
